import {
	ChangeDetectionStrategy,
	Component,
	computed,
	HostBinding,
	OnDestroy,
	signal,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FeatureService } from '../shared/services/feature.service';
import { DataService } from '../shared/services/data.service';
import { I18nKeys } from '../shared/models/i18n.model';
import {
	CALLBACK_PHONE_MAX_LENGTH,
	CALLBACK_PHONE_PATTERN,
	DetailedFeedbackRequestDTO,
	FeedbackCallnoteDTO,
	ICheckboxes,
	IFeedbackElement,
	POSITIVE_RATING_THRESHOLD,
	RATINGS,
	RatingConfig,
} from '../shared/models/feedback.model';

@Component({
	selector: 'app-callnote-feedback',
	standalone: false,
	templateUrl: './callnote-feedback.html',
	styleUrl: './callnote-feedback.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallnoteFeedback implements OnDestroy {
	@HostBinding('class.app-callnote-feedback') default = true;

	private subscriptions = new Subscription();
	private uid = '';

	public I18N = I18nKeys;
	public readonly ratings: RatingConfig[] = RATINGS;

	public currentRating = signal(0);
	public isReadOnly = signal(false);
	public detailedSaved = signal(false);
	public savingDetailed = signal(false);
	public saveError = signal(false);
	public editUnavailable = new URLSearchParams(location.search).has('correct');
	public feedbackData = signal<FeedbackCallnoteDTO | null>(null);

	public isPositive = computed(() => this.currentRating() >= POSITIVE_RATING_THRESHOLD);

	public form = new FormGroup({
		wrongNameOrCompany: new FormControl(false, { nonNullable: true }),
		wrongTelephoneNumber: new FormControl(false, { nonNullable: true }),
		wrongNotice: new FormControl(false, { nonNullable: true }),
		unfriendlyCall: new FormControl(false, { nonNullable: true }),
		ordersIgnored: new FormControl(false, { nonNullable: true }),
		customerAnnotation: new FormControl('', { nonNullable: true }),
		contactWish: new FormControl(false, { nonNullable: true }),
		callbackPhoneNumber: new FormControl('', {
			nonNullable: true,
			validators: [Validators.maxLength(CALLBACK_PHONE_MAX_LENGTH), Validators.pattern(CALLBACK_PHONE_PATTERN)],
		}),
	});

	constructor(
		private readonly featureService: FeatureService,
		private readonly dataService: DataService,
	) {
		this.subscriptions.add(
			this.featureService.feedbackData$.subscribe((data) => {
				this.feedbackData.set(data);
				this.uid = new URLSearchParams(location.search).get('uid') || '';
				const urlRating = +(new URLSearchParams(location.search).get('rating') || '0');
				const hasExistingFeedback = data.feedback && 'rating' in data.feedback;

				if (hasExistingFeedback) {
					const existing = data.feedback as IFeedbackElement;
					this.applyExistingFeedback(existing);

					// URL rating differs from stored rating?
					if (urlRating && urlRating !== existing.rating) {
						if (!this.isDetailedFeedbackGiven(existing)) {
							// No detailed feedback yet → update simple rating
							this.currentRating.set(urlRating);
							this.dataService.saveSimpleRating$(this.uid, urlRating).subscribe({
							error: () => this.saveError.set(true),
						});
						}
						// Detailed feedback exists → read-only, no change (already handled above)
					}
				} else if (urlRating) {
					// First visit — no feedback yet → save initial rating
					this.currentRating.set(urlRating);
					this.dataService.saveSimpleRating$(this.uid, urlRating).subscribe({
							error: () => this.saveError.set(true),
						});
				}
			}),
		);
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	onRatingClick(rating: number): void {
		if (this.isReadOnly()) return;
		this.saveError.set(false);
		this.currentRating.set(rating);
		this.dataService.saveSimpleRating$(this.uid, rating).subscribe({
			error: () => this.saveError.set(true),
		});
	}

	submitDetailedFeedback(): void {
		if (this.isReadOnly() || this.savingDetailed() || this.form.invalid) return;
		this.savingDetailed.set(true);
		this.saveError.set(false);

		const formValue = this.form.getRawValue();
		const hasCustomPhone = formValue.callbackPhoneNumber.trim().length > 0;
		const payload: DetailedFeedbackRequestDTO = {
			rating: this.currentRating(),
			customerAnnotation: formValue.customerAnnotation,
			contactWish: formValue.contactWish || hasCustomPhone,
			callbackPhoneNumber: hasCustomPhone ? formValue.callbackPhoneNumber.trim() : '',
			praise: this.currentRating() === 5,
			detailedRating: {
				wrongNameOrCompany: formValue.wrongNameOrCompany,
				wrongTelephoneNumber: formValue.wrongTelephoneNumber,
				wrongNotice: formValue.wrongNotice,
				unfriendlyCall: formValue.unfriendlyCall,
				ordersIgnored: formValue.ordersIgnored,
			},
		};

		this.dataService.saveDetailedFeedback$(payload, this.uid).subscribe({
			next: () => {
				this.savingDetailed.set(false);
				this.detailedSaved.set(true);
				this.isReadOnly.set(true);
				this.form.disable();
			},
			error: () => {
				this.savingDetailed.set(false);
				this.saveError.set(true);
			},
		});
	}

	private applyExistingFeedback(feedback: IFeedbackElement): void {
		this.currentRating.set(feedback.rating);

		this.form.patchValue({
			wrongNameOrCompany: feedback.detailedRating?.wrongNameOrCompany ?? false,
			wrongTelephoneNumber: feedback.detailedRating?.wrongTelephoneNumber ?? false,
			wrongNotice: feedback.detailedRating?.wrongNotice ?? false,
			unfriendlyCall: feedback.detailedRating?.unfriendlyCall ?? false,
			ordersIgnored: feedback.detailedRating?.ordersIgnored ?? false,
			customerAnnotation: feedback.customerAnnotation ?? '',
			contactWish: feedback.contactWish ?? false,
			callbackPhoneNumber: feedback.callbackPhoneNumber ?? '',
		});

		if (this.isDetailedFeedbackGiven(feedback)) {
			this.isReadOnly.set(true);
			this.detailedSaved.set(true);
			this.form.disable();
		}
	}

	private isDetailedFeedbackGiven(feedback: IFeedbackElement): boolean {
		return (
			(feedback.customerAnnotation?.length ?? 0) > 0 ||
			this.hasNegativeAspects(feedback.detailedRating) ||
			feedback.contactWish
		);
	}

	private hasNegativeAspects(checkboxes: ICheckboxes | null | undefined): boolean {
		if (!checkboxes) return false;
		return (
			checkboxes.ordersIgnored ||
			checkboxes.unfriendlyCall ||
			checkboxes.wrongNameOrCompany ||
			checkboxes.wrongNotice ||
			checkboxes.wrongTelephoneNumber
		);
	}
}
